import PolicyPage from "@/components/shared/PolicyPage";

const SECTIONS = [
  {
    p: [
      'At Carztohire ("Carztohire," "Company," "we," "our," or "us"), we recognize the importance of protecting your personal information and respecting your privacy. This Privacy Policy explains how we collect, use, disclose, store, and safeguard your information when you visit our website, communicate with us, or use any of our vehicle rental, reservation, customer support, or related services (collectively, the "Services").',
      "By accessing or using our website or Services, you acknowledge that you have read and understood this Privacy Policy and agree to the collection and use of your information as described herein. If you do not agree with any part of this Privacy Policy, please discontinue use of our Services. This Privacy Policy should be read together with our Terms and Conditions and any additional policies referenced on our website.",
    ],
  },
  {
    h: "Information we collect",
    p: "To provide our Services efficiently, we collect information that identifies, relates to, or can reasonably be linked to an individual. Depending on how you interact with Carztohire, the information we collect may include the following categories.",
    sh: "Personal information",
  },
  {
    p: "The personal information we collect may include:",
    list: [
      "Full name",
      "Residential, billing, and mailing addresses",
      "Telephone numbers",
      "Email address",
      "Driver's license information and other government-issued identification when required for vehicle rental verification",
      "Passport details, where necessary for certain bookings or identity verification",
      "Payment and billing information used to complete transactions",
      "Rental history and reservation details",
      "Pickup and return locations, dates, and times",
      "Vehicle preferences and special requests",
      "Emergency contact information, if provided",
      "Date of birth",
      "Nationality or residency information, where required",
      "Customer account credentials",
      "Communication preferences",
      "Records of correspondence with our customer support team",
    ],
  },
  { p: "If you provide information on behalf of another individual, such as an additional driver or passenger, you represent that you have obtained their authorization to share their information with us and that they understand how their information may be used under this Privacy Policy." },
  {
    sh: "Payment information",
    p: "When processing reservations or purchases, Carztohire may collect payment-related information such as credit or debit card details and billing information. We do not permanently store complete payment card information on our own systems unless required for authorized business purposes. Payments are processed through secure payment providers that comply with applicable Payment Card Industry Data Security Standards (PCI DSS) designed to protect sensitive financial information.",
  },
  {
    sh: "Vehicle rental information",
    p: "To complete vehicle reservations and satisfy legal or contractual obligations, we may collect information including:",
    list: [
      "Driver's license details",
      "License issuing authority",
      "License expiration date",
      "Rental eligibility information",
      "Insurance details, where applicable",
      "Vehicle selection",
      "Rental duration",
      "Pickup and return locations",
      "Reservation confirmation information",
    ],
  },
  { p: "This information is necessary to verify eligibility, process reservations, and comply with applicable laws and insurance requirements." },
  {
    sh: "Information collected automatically",
    p: "When you visit our website, certain technical information may be collected automatically through your browser, device, or network connection. This information may include:",
    list: [
      "IP address",
      "Browser type and version",
      "Operating system",
      "Device identifiers",
      "Mobile device information",
      "Internet service provider",
      "Referring website",
      "Pages viewed",
      "Date and time of your visit",
      "Session duration",
      "Clickstream data",
      "Search queries performed on our website",
      "Website interactions",
      "Error logs",
      "Diagnostic information",
    ],
  },
  {
    sh: "Cookies and similar technologies",
    p: "Carztohire uses cookies and similar technologies to improve website functionality and personalize your browsing experience. Please review our Cookie Policy for full details.",
  },
  {
    sh: "Information received from third parties",
    p: "We may also receive information from trusted third parties, including:",
    list: [
      "Vehicle rental partners",
      "Payment processors",
      "Identity verification providers",
      "Marketing partners",
      "Customer referral partners",
      "Business affiliates",
      "Social media platforms",
      "Public databases",
      "Fraud prevention services",
      "Analytics providers",
    ],
  },
  { p: "Information obtained from these sources may be combined with information you provide directly to us to improve the accuracy of our records, prevent fraud, personalize our Services, and enhance customer support." },
  {
    sh: "Information you choose to provide",
    p: "You may voluntarily provide information when you make a reservation, request a vehicle quote, contact customer support, complete online forms, subscribe to newsletters, participate in surveys, enter promotional campaigns, leave reviews or feedback, contact us through social media, apply for employment opportunities, or communicate with us by telephone, email, or other methods. You may choose not to provide certain information; however, doing so may limit our ability to provide some Services or complete your reservation.",
  },
  {
    h: "How we use your information",
    p: "Carztohire uses the information we collect to operate our business, provide our Services, fulfill vehicle reservations, improve customer experiences, and comply with applicable legal obligations. Depending on your interaction with us, we may use your personal information for one or more of the following purposes.",
    sh: "Reservation and rental services",
    list: [
      "Process vehicle rental reservations and confirmations.",
      "Verify your identity and driving eligibility.",
      "Coordinate vehicle pickup and return arrangements.",
      "Manage reservation modifications, cancellations, and extensions.",
      "Process payments and issue refunds where applicable.",
      "Provide invoices, receipts, and rental documentation.",
      "Facilitate communication regarding your reservation before, during, and after your rental period.",
    ],
  },
  {
    sh: "Customer support",
    list: [
      "Responding to questions and requests.",
      "Resolving booking or payment issues.",
      "Investigating complaints.",
      "Providing roadside assistance support when applicable.",
      "Following up on customer feedback.",
      "Maintaining records of customer interactions for quality assurance and service improvement.",
    ],
  },
  {
    sh: "Account administration",
    list: [
      "Create and maintain your account.",
      "Authenticate your identity during sign-in.",
      "Protect your account from unauthorized access.",
      "Store your preferences and booking history.",
      "Allow you to manage upcoming reservations.",
      "Enable secure password recovery and account verification.",
    ],
  },
  {
    sh: "Communication",
    p: "We may contact you regarding your relationship with Carztohire, including to confirm reservations, send booking updates, notify you of schedule changes, provide rental reminders, respond to inquiries, inform you about important service announcements, notify you of updates to our policies or Terms of Service, and deliver security alerts or fraud notifications. These communications are considered necessary for the operation of our Services.",
  },
  {
    sh: "Marketing and promotional communications",
    p: "With your consent where required by law, we may use your information to send promotional emails, special offers, discount programs, seasonal promotions, loyalty rewards, vehicle upgrade opportunities, travel and rental recommendations, company news, and customer surveys. You may opt out of receiving promotional communications at any time by following the unsubscribe instructions included in our emails or by contacting us directly. Opting out of marketing communications will not affect essential service-related communications.",
  },
  {
    sh: "Personalization",
    list: [
      "Remembering your preferences.",
      "Displaying relevant vehicle options.",
      "Suggesting services based on previous rentals.",
      "Improving search results.",
      "Providing location-relevant content.",
      "Customizing website features.",
      "Offering promotions that may be of interest to you.",
    ],
  },
  {
    sh: "Website improvement",
    list: [
      "Improve website functionality.",
      "Enhance navigation.",
      "Optimize page performance.",
      "Correct technical issues.",
      "Improve mobile compatibility.",
      "Test new features.",
      "Analyze user behavior.",
      "Increase overall customer satisfaction.",
    ],
  },
  {
    sh: "Business analytics",
    list: [
      "Analyze customer trends.",
      "Forecast demand.",
      "Measure booking activity.",
      "Evaluate marketing campaigns.",
      "Monitor business performance.",
      "Improve pricing strategies.",
      "Develop new products and services.",
      "Generate internal reports.",
    ],
  },
  { p: "Whenever possible, analytical reports use aggregated or de-identified information." },
  {
    sh: "Fraud prevention and security",
    list: [
      "Detect fraudulent reservations.",
      "Verify customer identities.",
      "Prevent unauthorized transactions.",
      "Investigate suspicious activity.",
      "Monitor account security.",
      "Prevent abuse of our Services.",
      "Protect company assets.",
      "Enforce our agreements.",
      "Reduce financial risk.",
    ],
  },
  {
    sh: "Legal and regulatory compliance",
    list: [
      "Comply with applicable laws and regulations.",
      "Respond to lawful requests from government authorities.",
      "Meet tax and accounting obligations.",
      "Fulfill insurance requirements.",
      "Maintain required business records.",
      "Resolve disputes.",
      "Enforce our legal rights.",
      "Defend against legal claims.",
      "Protect the safety of our customers, employees, and business.",
    ],
  },
  {
    h: "Legal basis for processing personal information",
    p: "Where required by applicable privacy laws, Carztohire processes personal information based on one or more of the following legal grounds:",
    list: [
      "Performance of a contract, such as processing vehicle reservations.",
      "Compliance with legal obligations.",
      "Protection of our legitimate business interests.",
      "Your consent, where required.",
      "Protection of vital interests.",
      "Other lawful bases permitted under applicable law.",
    ],
  },
  {
    h: "How we share your information",
    p: "Carztohire does not sell your personal information for monetary compensation. However, we may share your information with trusted third parties when necessary to operate our Services, fulfill reservations, or comply with legal obligations.",
  },
  {
    sh: "Vehicle rental and travel partners",
    p: "To complete your reservation, we may share relevant information with organizations involved in providing your rental services, including vehicle fleet providers, rental fulfillment partners, insurance providers, roadside assistance providers, reservation management companies, airport service providers, and vehicle maintenance vendors where necessary. Only the information reasonably required to complete your reservation or provide related services will be shared.",
  },
  {
    sh: "Payment service providers",
    p: "Payments are processed through secure third-party payment providers. Information shared may include customer name, billing information, transaction amount, payment authorization details, and fraud prevention information. Payment processors are responsible for protecting payment information in accordance with applicable security standards.",
  },
  {
    sh: "Technology service providers",
    p: "We work with third-party vendors that assist us in operating our business — website hosting, cloud storage, customer relationship management, technical support, email delivery, SMS notifications, fraud monitoring, data analytics, security monitoring, customer support platforms, and software maintenance. These providers are contractually obligated to safeguard your information and may use it only to perform services on our behalf.",
  },
  {
    sh: "Marketing partners",
    p: "We may work with marketing agencies, advertising platforms, analytics providers, and promotional partners to measure advertising performance, deliver relevant advertisements, improve marketing campaigns, analyze website traffic, and evaluate customer engagement. Where required by law, these activities are conducted based on your consent or other lawful processing grounds.",
  },
  {
    sh: "Business affiliates",
    p: "We may share information with affiliated companies that support our operations, provided they process the information in accordance with this Privacy Policy and applicable law.",
  },
  {
    sh: "Corporate transactions",
    p: "If Carztohire undergoes a merger, acquisition, financing transaction, corporate restructuring, bankruptcy proceeding, or sale of assets, customer information may be transferred as part of that transaction. Any successor organization will continue to protect your information in accordance with applicable privacy laws.",
  },
  {
    sh: "Legal requirements",
    p: "We may disclose personal information when we reasonably believe disclosure is necessary to comply with legal obligations, respond to subpoenas or court orders, cooperate with law enforcement agencies, protect public safety, prevent fraud or financial crimes, investigate violations of our policies, protect our legal rights, enforce our agreements, or defend against legal claims.",
  },
  {
    sh: "With your consent",
    p: "In situations where your consent is required, we will obtain your authorization before sharing your personal information for purposes not otherwise described in this Privacy Policy.",
  },
  {
    h: "Third-party services and social media",
    p: "Our website and Services may contain links to websites, applications, or services operated by third parties. These third parties maintain their own privacy practices and terms of use, which are independent of Carztohire. We encourage you to review the privacy policies of any external websites or services before providing personal information. Carztohire is not responsible for the privacy, security, content, or practices of third-party websites, mobile applications, advertising networks, payment platforms, social media services, or other organizations that are not owned or controlled by us. The inclusion of a third-party link on our website does not constitute an endorsement, sponsorship, or recommendation of that third party or its products and services.",
  },
  {
    h: "Location information",
    p: "Depending on your device settings and permissions, Carztohire may collect approximate or precise location information. Location data may be used to display nearby rental locations, improve reservation accuracy, customize available services, detect potentially fraudulent activity, enhance customer support, and improve marketing relevance. You may disable location sharing through your device settings; however, some features of our Services may not function as intended.",
  },
  {
    h: "Interest-based advertising",
    p: "Carztohire may work with advertising partners, analytics providers, and digital marketing platforms to display advertisements that are more relevant to your interests. Advertising may be based on factors such as browsing activity, reservation history, website interactions, device information, approximate location, marketing preferences, and anonymous audience segments. Our advertising partners may use cookies, pixels, SDKs, APIs, and similar technologies to measure campaign performance and deliver personalized advertisements across websites and applications.",
  },
  {
    h: "Data security",
    p: "Protecting your information is an important part of our business operations. Carztohire maintains administrative, technical, and physical safeguards designed to protect personal information against unauthorized access, disclosure, alteration, misuse, or destruction. Our security measures may include:",
    list: [
      "Secure encrypted connections (SSL/TLS)",
      "Access controls",
      "Employee confidentiality obligations",
      "Network monitoring",
      "Firewalls",
      "Security logging",
      "Multi-layer authentication where appropriate",
      "Secure payment processing",
      "Routine security reviews",
      "Vendor security assessments",
    ],
  },
  { p: "Although we take reasonable measures to safeguard your information, no method of electronic transmission or storage is completely secure. Therefore, we cannot guarantee absolute security. If you believe your account or personal information has been compromised, please contact us immediately using the contact information provided at the end of this Privacy Policy." },
  {
    h: "Your privacy rights",
    p: "Depending on your country, state, or province of residence, you may have certain rights regarding your personal information. These rights may include the ability to:",
    list: [
      "Request access to your personal information.",
      "Request correction of inaccurate information.",
      "Request deletion of eligible personal information.",
      "Request a copy of your personal information.",
      "Request information about how your data is processed.",
      "Object to certain types of processing.",
      "Restrict processing under applicable laws.",
      "Withdraw consent where processing is based on consent.",
      "Opt out of certain marketing communications.",
      "Lodge a complaint with an applicable data protection authority.",
    ],
  },
  { p: "These rights may be subject to legal limitations and exceptions." },
  {
    h: "Accessing, updating, or deleting your information",
    p: "If you would like to access, update, correct, or request deletion of your personal information, you may contact Carztohire using the contact details provided below. For security purposes, we may ask you to verify your identity before processing your request. Depending on applicable legal requirements, we will respond within the timeframes required by law. Please note that we may retain certain information when necessary to complete ongoing transactions, comply with legal obligations, resolve disputes, prevent fraud, enforce our agreements, maintain required business records, and protect our legal rights.",
  },
  {
    h: "Marketing communications",
    p: "If you no longer wish to receive promotional emails from Carztohire, you may unsubscribe at any time by clicking the \"Unsubscribe\" link included in our marketing emails, or by contacting us directly using the contact information below. Even if you opt out of promotional communications, we may continue sending important service-related communications, including reservation confirmations, billing notices, security alerts, policy updates, and customer support responses.",
  },
  {
    h: "Data retention",
    p: "Carztohire retains personal information only for as long as necessary to fulfill the purposes described in this Privacy Policy, including providing our Services, maintaining business records, resolving disputes, enforcing agreements, and complying with applicable legal, tax, accounting, and regulatory obligations. When personal information is no longer required, we will securely delete, anonymize, or otherwise dispose of it in accordance with applicable laws and our internal data retention practices.",
  },
  {
    h: "Children's privacy",
    p: "Our Services are intended for individuals who are at least 18 years of age. We do not knowingly collect personal information from children under the age of 18. If we become aware that we have collected personal information from a child without appropriate authorization, we will take reasonable steps to delete the information as soon as practicable. If you believe a child has provided personal information to Carztohire, please contact us immediately.",
  },
  {
    h: "International users",
    p: "If you access our Services from outside the United States, please be aware that your information may be transferred to, processed, and stored in the United States or other countries where Carztohire or its service providers operate. Data protection laws in these jurisdictions may differ from those in your country of residence. By using our Services, where permitted by applicable law, you consent to the transfer and processing of your information as described in this Privacy Policy.",
  },
  {
    h: "Changes to this privacy policy",
    p: "Carztohire may update this Privacy Policy from time to time to reflect changes in our business practices, technology, legal requirements, or operational needs. When material changes are made, we will update the Effective Date at the top of this Privacy Policy. Where required by law, we may also provide additional notice through our website or other appropriate communication channels. Your continued use of our Services after an updated Privacy Policy becomes effective constitutes your acceptance of the revised policy.",
  },
  {
    h: "Contact us",
    p: [
      "If you have any questions, comments, or requests regarding this Privacy Policy or the way Carztohire handles your personal information, please contact us using the information below:",
      "Carztohire — 1736 Holly Oak Ln, Chattanooga, TN 37421, United States",
      "Phone: (602) 610-1396 · Email: info@carztohire.com",
      "We will make reasonable efforts to respond to your inquiry as promptly as possible.",
    ],
  },
];

export default function PrivacyPolicy() {
  return (
    <PolicyPage
      overline="Legal · Privacy Policy"
      title="Privacy Policy."
      intro="How Carztohire collects, uses, discloses, stores, and safeguards your information when you use our Services."
      effectiveDate="01 July 2026"
      sections={SECTIONS}
      testId="privacy-policy"
    />
  );
}
